import { CAPSTONE, CRITERION, fmt } from "./progress";

/**
 * Explains a student's grade: where each certificate requirement stands, the sum behind the
 * activities score, and what every piece of work contributes. Takes summarize(plan) output.
 */

const st = (cls, text) => <span className={`st ${cls}`}>{text}</span>;
const num = (v) => (v == null ? "—" : Number.isInteger(v) ? v : v.toFixed(1));
const KIND = { quiz: "Quiz", assignment: "Assignment", project: "Project" };
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : null);

const capCls = (cap) => (cap.status === "passed" ? "done" : cap.status === "redo_required" ? "due" : cap.status === "submitted" ? "today" : "open");
const statusPill = (status) => st(...(CRITERION[status] || CRITERION.not_started));

function Meter({ label, value, pass, status, children }) {
  const ok = value != null && value >= pass;
  return (
    <div className="req-row">
      <div className="req-top"><strong>{label} {statusPill(status)}</strong><b className={value == null ? "" : ok ? "ok" : "low"}>{fmt(value)}</b></div>
      <div className="req-bar" role="img" aria-label={`${label} ${fmt(value)}, need ${pass}%`}><i style={{ width: `${Math.min(value || 0, 100)}%` }} /><em style={{ left: `${pass}%` }} /></div>
      {children}
    </div>
  );
}

/** The three requirements, each with how its number is worked out. */
export function CertificateProgress({ g }) {
  const { criteria: c, attendance: att, activities: act, capstone: cap, ended } = g;
  const b = act.breakdown;
  return (
    <div className="req">
      <Meter label="Attendance" value={att.percent} pass={c.attendance_pass} status={att.status}>
        <small>
          {att.attended} of {att.held} classes so far
          {att.live != null && ` · ${att.live} live, ${att.recording} from the recording`} · need at least {c.attendance_pass}%.
          {" "}A missed class counts when you watch at least {c.watch_percent}% of its recording. Each class counts on its own.
        </small>
      </Meter>

      <Meter label={ended ? "Activities (final)" : "Activities (current)"} value={act.score} pass={c.activities_pass} status={act.status}>
        {b && b.score != null ? (
          <div className="calc">
            {b.quiz_average != null && <span>Quiz average <b>{num(b.quiz_average)}</b>{b.assignment_average != null && <> × {c.quiz_weight}%</>}</span>}
            {b.quiz_average != null && b.assignment_average != null && <span className="op">+</span>}
            {b.assignment_average != null && <span>Assignment average <b>{num(b.assignment_average)}</b>{b.quiz_average != null && <> × {c.assignment_weight}%</>}</span>}
            <span className="op">=</span><span><b>{num(b.score)}</b></span>
          </div>
        ) : null}
        <small>
          {ended
            ? `Every quiz and assignment counts. Anything not submitted or not graded counts as 0. Need ${c.activities_pass} or more.`
            : `Counts only work past its deadline (${b?.quizzes_counted ?? 0} quizzes, ${b?.assignments_counted ?? 0} assignments so far). Anything you missed counts as 0. Need ${c.activities_pass} or more by the end of the course.`}
          {!ended && act.awaiting > 0 && ` ${act.awaiting} submitted ${act.awaiting === 1 ? "piece" : "pieces"} of work ${act.awaiting === 1 ? "is" : "are"} waiting to be graded and will count once marked.`}
        </small>
      </Meter>

      <div className="req-row">
        <div className="req-top"><strong>Capstone project</strong>{st(capCls(cap), cap.grade ? `${cap.grade} · ${CAPSTONE[cap.status]}` : CAPSTONE[cap.status])}</div>
        <small>Earn an A, B or C. A D or F means a redo.</small>
      </div>
    </div>
  );
}

/** What each quiz and assignment scored and what it adds to the activities score. */
export function WorkTable({ g, go, course }) {
  const status = (i) => {
    if (i.status === "submitted") return st("today", "Waiting to be graded");
    if (i.status === "missing") return g.ended || i.past_deadline ? st("due", i.type === "quiz" ? "Not taken · counts as 0" : "Not submitted · counts as 0") : st("open", i.deadline ? `Due ${day(i.deadline)}` : "Not due yet");
    return i.counts == null ? st("open", "Counts after the deadline") : st("done", "Counted");
  };
  return (
    <div className="plain">
      <table className="gtable work-table">
        <thead><tr><th>Work</th>{course && <th>Course</th>}<th>Result</th><th>Status</th><th>Points</th></tr></thead>
        <tbody>
          {g.items.length ? g.items.map((i) => (
            <tr key={`${i.type}-${i.id}`}>
              <td>
                {go ? <button className="link-btn gwork" type="button" onClick={() => go(i.to)}>{i.title}</button> : i.title}
                <small>{KIND[i.type]}{i.deadline ? ` · deadline ${day(i.deadline)}` : ""}{i.note ? ` · ${i.note}` : ""}</small>
              </td>
              {course && <td><span className="course-tag">{course}</span></td>}
              <td>{i.result || "—"}</td>
              <td>{status(i)}</td>
              <td>{i.counts == null ? "—" : num(i.counts)}</td>
            </tr>
          )) : <tr><td colSpan={course ? 5 : 4} className="gempty">No quizzes or assignments yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/** The rules: weights, letter grade points and the capstone pass mark. */
export function HowGraded({ criteria: c }) {
  return (
    <section className="card">
      <div className="card-h"><h3>How to earn a certificate</h3></div>
      <ol className="req-steps">
        <li><b>{c.attendance_pass}%+</b><div><strong>Attendance</strong><small>Attend at least {c.attendance_pass}% of live classes. Missed one? Watch at least {c.watch_percent}% of the recording and it counts.</small></div></li>
        <li><b>{c.activities_pass}%+</b><div><strong>Activities</strong><small>Your quizzes count for {c.quiz_weight}% and your assignments count for {c.assignment_weight}%. Reach a combined {c.activities_pass}% to complete this.</small></div></li>
        <li><b>Pass</b><div><strong>Capstone</strong><small>Earn an A, B or C. A D or F means a redo.</small></div></li>
      </ol>
      <small className="muted-note">All three must show Met for your certificate to unlock. Anything not submitted or not graded counts as 0 in your final activities score.</small>
      <table className="gtable letters">
        <thead><tr><th>Assignment grade</th><th>Band</th><th>Points it's worth</th><th>Capstone</th></tr></thead>
        <tbody>
          {c.letters.map((l) => (
            <tr key={l.letter}><td><b>{l.letter}</b></td><td>{l.band}</td><td>{l.points}</td><td>{l.capstone === "pass" ? st("done", "Pass") : st("due", "Redo")}</td></tr>
          ))}
        </tbody>
      </table>
      <small className="muted-note">Quizzes count their percentage score (your best attempt). Scores are rounded to one decimal place.</small>
    </section>
  );
}

/** Compact chips for cards. */
export function ReqChips({ g }) {
  const { criteria: c } = g;
  return (
    <div className="req-mini">
      <span className={g.attendance.met ? "ok" : ""}>Attendance {fmt(g.attendance.percent)} · need {c.attendance_pass}% · {CRITERION[g.attendance.status]?.[1]}{g.attendance.met ? " ✓" : ""}</span>
      <span className={g.activities.met ? "ok" : ""}>Activities {fmt(g.activities.score)} · need {c.activities_pass}% · {CRITERION[g.activities.status]?.[1]}{g.activities.met ? " ✓" : ""}</span>
      <span className={g.capstone.met ? "ok" : ""}>Capstone · {(g.capstone.grade ? `${g.capstone.grade}, ` : "") + CAPSTONE[g.capstone.status].toLowerCase()}{g.capstone.met ? " ✓" : ""}</span>
    </div>
  );
}
